import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Instagram, LogOut, LinkIcon, Pencil, Check, X } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'

interface UserConfig {
  email: string
  instagram_username: string | null
  profile_picture_url: string
  dm_message: string
  comment_reply: string
  is_active: boolean
  reply_active: boolean
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { logout } = useAuth()

  const [config, setConfig] = useState<UserConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)

  // Edit state (only active when editing)
  const [dmMessage, setDmMessage] = useState('')
  const [commentReply, setCommentReply] = useState('')

  const loadConfig = useCallback(async () => {
    try {
      const data = await api.me()
      const mapped: UserConfig = {
        email: data.user?.email || '',
        instagram_username: data.ig?.username || null,
        profile_picture_url: data.ig?.profile_picture_url || '',
        dm_message: data.ig?.dm_message || '',
        comment_reply: data.ig?.comment_reply || '',
        is_active: data.ig?.is_active ?? false,
        reply_active: data.ig?.reply_active ?? true,
      }
      setConfig(mapped)
    } catch (err: any) {
      if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.message?.includes('token')) {
        api.clearToken()
        navigate('/')
      } else {
        toast.error(err.message)
      }
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  // Handle OAuth redirect-back
  useEffect(() => {
    const connected = searchParams.get('connected')
    const error = searchParams.get('error')
    if (connected === '1') {
      setSearchParams({}, { replace: true })
      loadConfig()
      toast.success('Instagram conectado!')
    } else if (error) {
      setSearchParams({}, { replace: true })
      if (error === 'no_business_account') {
        toast.error('Conta Instagram não é Business/Creator. Converta sua conta no app do Instagram e tente novamente.')
      } else {
        toast.error('Erro ao conectar Instagram. Tente novamente.')
      }
    }
  }, [searchParams, setSearchParams, loadConfig])

  function handleStartEdit() {
    if (!config) return
    setDmMessage(config.dm_message)
    setCommentReply(config.comment_reply)
    setEditing(true)
  }

  function handleCancelEdit() {
    setEditing(false)
  }

  async function handleSave() {
    if (!config) return
    setSaving(true)
    try {
      await api.saveConfig({
        dm_message: dmMessage,
        comment_reply: commentReply,
        is_active: config.is_active,
        reply_active: config.reply_active,
      })
      setConfig(prev => prev ? { ...prev, dm_message: dmMessage, comment_reply: commentReply } : prev)
      setEditing(false)
      toast.success('Salvo!')
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar.')
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleDm(value: boolean) {
    if (!config) return
    setConfig(prev => prev ? { ...prev, is_active: value } : prev)
    try {
      await api.saveConfig({
        dm_message: config.dm_message,
        comment_reply: config.comment_reply,
        is_active: value,
        reply_active: config.reply_active,
      })
    } catch (err: any) {
      setConfig(prev => prev ? { ...prev, is_active: !value } : prev)
      toast.error('Erro ao salvar.')
    }
  }

  async function handleToggleReply(value: boolean) {
    if (!config) return
    setConfig(prev => prev ? { ...prev, reply_active: value } : prev)
    try {
      await api.saveConfig({
        dm_message: config.dm_message,
        comment_reply: config.comment_reply,
        is_active: config.is_active,
        reply_active: value,
      })
    } catch (err: any) {
      setConfig(prev => prev ? { ...prev, reply_active: !value } : prev)
      toast.error('Erro ao salvar.')
    }
  }

  async function handleDisconnect() {
    try {
      await api.disconnect()
      await loadConfig()
      toast.success('Instagram desconectado.')
    } catch (err: any) {
      toast.error(err.message)
    }
  }

  function handleLogout() {
    api.clearToken()
    logout()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 bg-black/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src="/logo.png" alt="Missão 14" className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            {config && (
              <span className="text-zinc-500 text-sm hidden sm:block">{config.email}</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white gap-1.5"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        {loading ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72" />
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        ) : !config?.instagram_username ? (
          /* State A: Not connected */
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center py-8 space-y-5">
                <div className="w-16 h-16 rounded-full bg-pink-950/40 border border-pink-900/50 flex items-center justify-center">
                  <Instagram className="w-8 h-8 text-pink-400" />
                </div>
                <div className="space-y-1.5">
                  <h2 className="text-xl font-semibold text-white">Conecte seu Instagram</h2>
                  <p className="text-zinc-500 text-sm max-w-sm">
                    Quando alguém comentar nos seus posts, a SIMIA manda o link automaticamente.
                  </p>
                </div>
                <Button
                  onClick={() => window.location.href = api.getOAuthUrl()}
                  className="bg-[#1877f2] hover:bg-[#166fe5] text-white font-semibold gap-2 px-6"
                >
                  <LinkIcon className="w-4 h-4" />
                  Conectar com Instagram
                </Button>
                <p className="text-xs text-zinc-700">Você será redirecionado para o login do Instagram / Meta</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* State B: Connected */
          <>
            {/* Account card */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {config.profile_picture_url ? (
                    <img
                      src={config.profile_picture_url}
                      alt={config.instagram_username}
                      className="w-10 h-10 rounded-full object-cover border-2 border-zinc-700"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                      <Instagram className="w-5 h-5 text-zinc-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-white text-sm">@{config.instagram_username}</p>
                    <p className="text-xs text-green-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                      Conectado
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDisconnect}
                  className="text-red-400 hover:text-red-300 hover:bg-red-950/30 text-xs"
                >
                  Desconectar
                </Button>
              </CardContent>
            </Card>

            {/* DM card */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Direct Message</p>
                    <p className="text-xs text-zinc-500">Mensagem enviada automaticamente por DM</p>
                  </div>
                  <Switch
                    checked={config.is_active}
                    onCheckedChange={handleToggleDm}
                  />
                </div>
                <Separator className="bg-zinc-800" />
                {editing ? (
                  <Textarea
                    placeholder="Cole o link ou mensagem enviada automaticamente por DM..."
                    value={dmMessage}
                    onChange={e => setDmMessage(e.target.value)}
                    className="min-h-[100px] resize-none"
                  />
                ) : (
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap min-h-[40px]">
                    {config.dm_message || <span className="text-zinc-600 italic">Nenhuma mensagem configurada</span>}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Reply card */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Resposta no Comentário</p>
                    <p className="text-xs text-zinc-500">Resposta pública deixada no comentário</p>
                  </div>
                  <Switch
                    checked={config.reply_active}
                    onCheckedChange={handleToggleReply}
                  />
                </div>
                <Separator className="bg-zinc-800" />
                {editing ? (
                  <Input
                    placeholder='ex: Mandei um link na sua DM! 😉'
                    value={commentReply}
                    onChange={e => setCommentReply(e.target.value)}
                  />
                ) : (
                  <p className="text-sm text-zinc-300">
                    {config.comment_reply || <span className="text-zinc-600 italic">Nenhuma resposta configurada</span>}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
              {editing ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleCancelEdit}
                    className="text-zinc-400 hover:text-white gap-1.5"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-white text-black hover:bg-zinc-100 gap-1.5"
                  >
                    <Check className="w-4 h-4" />
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={handleStartEdit}
                  className="border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 gap-1.5"
                >
                  <Pencil className="w-4 h-4" />
                  Editar mensagens
                </Button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
